import { Car } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import {
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import { OWNERSHIP_STEP, SUMMARY_STEP, TAGS_STEP, VALUE_STEP } from '../utils/steps'
import { WizardDefinition } from '../utils/types'

import { vehicleSchema } from './schema'
import { create } from './create'
import { TagRef } from '@/entities/tags/utils/types'

/**
 * What every vehicle this wizard creates is filed as. Asking for it would be
 * asking the user to repeat the wizard they just chose — and the answer is
 * not theirs to vary: a car is a car because this is the wizard they picked,
 * not a type they typed.
 *
 * The one asset type here with no legacy wizard behind it — the other five
 * reuse a string `modules/wizards` has been posting for years. If the
 * repository's `asset_types` does not carry this one, this is the line to
 * change.
 */
const VEHICLE_TYPE = 'vehicle'

const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Personal Property' },
  { tag_name: 'Investment Strategy', tag_value: 'Vehicle' },
]

/**
 * A car, boat or aircraft. Financing has no step here on purpose — the
 * assistant routes a financed vehicle to this wizard too, and a loan against it
 * is its own record, added with the loan wizard.
 */
export const vehicleWizard: WizardDefinition = {
  flow: AssetWizardFlow.VEHICLE,
  label: 'Vehicle',
  icon: Car,
  schema: vehicleSchema,
  steps: [
    OWNERSHIP_STEP,
    {
      key: 'vehicle',
      label: 'Vehicle',
      heading: 'What is this?',
      description: 'Name it and record the purchase. Cars, boats and aircraft all go here — a loan against one is its own record.',
      fields: [
        amountField('Cost'),
        assetNameField('Vehicle name', 'Make and model'),
        dateField('Date of purchase'),
        currencyField(),
        { ...descriptionField(), span: 2 },
      ],
    },
    VALUE_STEP,
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'vehicle',
      label: 'Vehicle',
      icon: Car,
      tone: 'brand',
      transactionType: 'add position',
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
  ],
  constants: { assetType: VEHICLE_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
